import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Container,
  Tabs,
  Tab,
  Paper,
  TextField,
  Grid,
  Chip,
  Card,
  CardContent,
  CardMedia,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Alert
} from '@mui/material';
import { AccountCircle, Logout, Image, DateRange } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import authService from '../services/authService';
import drawingsService from '../services/drawingsService';

const Dashboard = ({ user, onLogout }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [currentTab, setCurrentTab] = useState(0);
  const [drawings, setDrawings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [hasServerError, setHasServerError] = useState(false);
  const [updatingPhase, setUpdatingPhase] = useState(false);
  const [phaseUpdateError, setPhaseUpdateError] = useState('');
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);

  const phases = ['initial', 'stage1', 'winner'];
  const phaseLabels = {
    initial: 'Initial',
    stage1: 'Stage 1',
    winner: 'Winner'
  };

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    await authService.logout();
    onLogout();
    handleClose();
  };

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  // Separate function for manual retry (not in useCallback to avoid dependency issues)
  const retryFetchDrawings = async () => {
    setHasServerError(false);
    await fetchDrawingsInternal();
  };

  const fetchDrawingsInternal = async () => {
    // Don't make requests if we've already encountered a server error
    if (hasServerError) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      const filters = {
        phase: phases[currentTab],
        limit: 100
      };

      if (selectedDate) {
        filters.date = selectedDate.format('YYYY-MM-DD');
      }

      const result = await drawingsService.getDrawings(filters);

      if (result.success) {
        setDrawings(result.data || []);
        // Reset server error flag on successful request
        setHasServerError(false);
      } else {
        setError(result.error || 'Failed to fetch drawings');
        setDrawings([]);
        
        // Check if this is a server error (500) and set flag to prevent retries
        if (result.error && (result.error.includes('500') || result.error.includes('Internal Server Error'))) {
          setHasServerError(true);
          setError('Server error detected. Please refresh the page to try again.');
        }
      }
    } catch (error) {
      setError('An unexpected error occurred');
      setDrawings([]);
      // Also set server error flag for unexpected errors
      setHasServerError(true);
    } finally {
      setLoading(false);
    }
  };

  const handleImageClick = (drawing) => {
    setSelectedImage(drawing);
    setImageModalOpen(true);
  };

  const handleImageModalClose = () => {
    setImageModalOpen(false);
    setSelectedImage(null);
    setPhaseUpdateError('');
  };

  const clearDateFilters = () => {
    setSelectedDate(null);
  };

  const fetchStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const filters = {};
      if (selectedDate) {
        filters.date = selectedDate.format('YYYY-MM-DD');
      }

      const params = new URLSearchParams();
      if (filters.date) {
        params.append('date', filters.date);
      }

      const result = await drawingsService.getDrawingsStats(params.toString() ? `?${params.toString()}` : '');
      
      if (result.success) {
        // Handle missing overallTotal by calculating from totals
        const statsData = result.data;
        if (statsData && !statsData.overallTotal && statsData.totals) {
          statsData.overallTotal = statsData.totals.reduce((sum, phase) => sum + (phase.total || 0), 0);
        }
        setStats(statsData);
      } else {
        console.error('Failed to fetch stats:', result.error);
        setStats(null);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
      setStats(null);
    } finally {
      setStatsLoading(false);
    }
  }, [selectedDate]);

  const getNextPhase = (currentPhase) => {
    const phaseOrder = ['initial', 'stage1', 'winner'];
    const currentIndex = phaseOrder.indexOf(currentPhase);
    if (currentIndex < phaseOrder.length - 1) {
      return phaseOrder[currentIndex + 1];
    }
    return currentPhase; // Already at the final phase
  };

  const getPreviousPhase = (currentPhase) => {
    const phaseOrder = ['initial', 'stage1', 'winner'];
    const currentIndex = phaseOrder.indexOf(currentPhase);
    if (currentIndex > 0) {
      return phaseOrder[currentIndex - 1];
    }
    return currentPhase; // Already at the initial phase
  };

  const handlePhaseUpdate = async (newPhase) => {
    if (!selectedImage) return;

    setUpdatingPhase(true);
    setPhaseUpdateError('');

    try {
      const result = await drawingsService.updateDrawingPhase(selectedImage.id, newPhase);
      
      if (result.success) {
        // Update the selected image with new phase
        setSelectedImage({ ...selectedImage, phase: newPhase });
        
        // Update the drawings list to reflect the change
        setDrawings(prevDrawings => 
          prevDrawings.map(drawing => 
            drawing.id === selectedImage.id 
              ? { ...drawing, phase: newPhase }
              : drawing
          )
        );
        
        // Refresh statistics to reflect the phase change
        fetchStats();
      } else {
        setPhaseUpdateError(result.error || 'Failed to update phase');
      }
    } catch (error) {
      setPhaseUpdateError('An unexpected error occurred');
    } finally {
      setUpdatingPhase(false);
    }
  };

  useEffect(() => {
    // Don't make requests if we've already encountered a server error
    if (hasServerError) {
      return;
    }

    const fetchData = async () => {
      setLoading(true);
      setError('');

      try {
        const filters = {
          phase: phases[currentTab],
          limit: 100
        };

        if (selectedDate) {
          filters.date = selectedDate.format('YYYY-MM-DD');
        }

        const result = await drawingsService.getDrawings(filters);

        if (result.success) {
          setDrawings(result.data || []);
          // Reset server error flag on successful request
          setHasServerError(false);
        } else {
          setError(result.error || 'Failed to fetch drawings');
          setDrawings([]);
          
          // Check if this is a server error (500) and set flag to prevent retries
          if (result.error && (result.error.includes('500') || result.error.includes('Internal Server Error'))) {
            setHasServerError(true);
            setError('Server error detected. Please refresh the page to try again.');
          }
        }
      } catch (error) {
        setError('An unexpected error occurred');
        setDrawings([]);
        // Also set server error flag for unexpected errors
        setHasServerError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [currentTab, selectedDate, hasServerError]);

  // Fetch statistics when component mounts or date filter changes
  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ flexGrow: 1 }}>
        <AppBar position="static">
          <Toolbar>
            <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
              Wish Tablet CMS
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="body2" sx={{ mr: 2 }}>
                {user?.email}
              </Typography>
              <IconButton
                size="large"
                aria-label="account of current user"
                aria-controls="menu-appbar"
                aria-haspopup="true"
                onClick={handleMenu}
                color="inherit"
              >
                <AccountCircle />
              </IconButton>
              <Menu
                id="menu-appbar"
                anchorEl={anchorEl}
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                keepMounted
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                open={Boolean(anchorEl)}
                onClose={handleClose}
              >
                <MenuItem onClick={handleLogout}>
                  <Logout sx={{ mr: 1 }} />
                  Logout
                </MenuItem>
              </Menu>
            </Box>
          </Toolbar>
        </AppBar>

        <Container maxWidth="xl" sx={{ mt: 3 }}>
          {/* Date Filters */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              <DateRange sx={{ mr: 1, verticalAlign: 'middle' }} />
              Date Filters
            </Typography>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6}>
                <DatePicker
                  label="Select Date"
                  value={selectedDate}
                  onChange={setSelectedDate}
                  enableAccessibleFieldDOMStructure={false}
                  slots={{
                    textField: TextField
                  }}
                  slotProps={{
                    textField: { fullWidth: true }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Button
                  variant="outlined"
                  onClick={clearDateFilters}
                  fullWidth
                >
                  Clear Date Filter
                </Button>
              </Grid>
            </Grid>
          </Paper>

          {/* Statistics Section */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Record Statistics
              {stats?.filteredByDate && (
                <Chip 
                  label={`Filtered by: ${dayjs(stats.filterDate).format('MMM DD, YYYY')}`}
                  size="small"
                  color="primary"
                  sx={{ ml: 2 }}
                />
              )}
            </Typography>
            
            {statsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={24} />
              </Box>
            ) : stats ? (
              <Grid container spacing={2}>
                {/* Overall Total */}
                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{ textAlign: 'center', bgcolor: 'primary.main', color: 'white' }}>
                    <CardContent sx={{ py: 2 }}>
                      <Typography variant="h4" component="div">
                        {stats.overallTotal || 0}
                      </Typography>
                      <Typography variant="body2">
                        Total Submissions
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                
                {/* Phase Counters */}
                {phases.map((phase) => {
                  const phaseStats = stats.totals?.find(t => t.phase === phase);
                  const count = phaseStats?.total || 0;
                  
                  return (
                    <Grid item xs={12} sm={6} md={3} key={phase}>
                      <Card 
                        sx={{ 
                          textAlign: 'center',
                          bgcolor: currentTab === phases.indexOf(phase) ? 'secondary.main' : 'grey.100',
                          color: currentTab === phases.indexOf(phase) ? 'white' : 'text.primary',
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          '&:hover': {
                            bgcolor: currentTab === phases.indexOf(phase) ? 'secondary.dark' : 'grey.200',
                          }
                        }}
                        onClick={() => setCurrentTab(phases.indexOf(phase))}
                      >
                        <CardContent sx={{ py: 2 }}>
                          <Typography variant="h4" component="div">
                            {count}
                          </Typography>
                          <Typography variant="body2">
                            {phaseLabels[phase]}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            ) : (
              <Alert severity="info">
                Unable to load statistics
              </Alert>
            )}
          </Paper>

          {/* Phase Tabs */}
          <Paper sx={{ mb: 3 }}>
            <Tabs
              value={currentTab}
              onChange={handleTabChange}
              variant="fullWidth"
              indicatorColor="primary"
              textColor="primary"
            >
              {phases.map((phase, index) => (
                <Tab
                  key={phase}
                  label={phaseLabels[phase]}
                  id={`phase-tab-${index}`}
                  aria-controls={`phase-tabpanel-${index}`}
                />
              ))}
            </Tabs>
          </Paper>

          {/* Content */}
          <Box>
            {error && (
              <Alert 
                severity="error" 
                sx={{ mb: 2 }}
                action={
                  hasServerError && (
                    <Button 
                      color="inherit" 
                      size="small" 
                      onClick={() => {
                        setError('');
                        retryFetchDrawings();
                      }}
                    >
                      Retry
                    </Button>
                  )
                }
              >
                {error}
              </Alert>
            )}

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <Grid container spacing={2}>
                {drawings.length === 0 ? (
                  <Grid item xs={12}>
                    <Paper sx={{ p: 4, textAlign: 'center' }}>
                      <Typography variant="h6" color="text.secondary">
                        No drawings found for {phaseLabels[phases[currentTab]]} phase
                      </Typography>
                    </Paper>
                  </Grid>
                ) : (
                  drawings.map((drawing) => (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={drawing.id}>
                      <Card sx={{ height: '100%', cursor: 'pointer' }}>
                        <CardMedia
                          component="img"
                          height="200"
                          image={drawing.gcs_url}
                          alt={drawing.filename}
                          onClick={() => handleImageClick(drawing)}
                          sx={{ 
                            objectFit: 'cover',
                            backgroundColor: '#000000',
                            cursor: 'pointer'
                          }}
                        />
                        <CardContent>
                          <Typography
                            variant="subtitle1"
                            component="div"
                            noWrap
                            onClick={() => handleImageClick(drawing)}
                            sx={{ 
                              cursor: 'pointer',
                              '&:hover': { textDecoration: 'underline' }
                            }}
                          >
                            {drawing.filename}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {dayjs(drawing.upload_date).format('dddd, D MMMM YYYY')}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                            {dayjs(drawing.upload_timestamp).add(7, 'hour').format('HH:mm')}
                          </Typography>
                          <Box sx={{ mt: 1 }}>
                            <Chip
                              label={phaseLabels[drawing.phase]}
                              size="small"
                              color="primary"
                              variant="outlined"
                            />
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))
                )}
              </Grid>
            )}
          </Box>
        </Container>

        {/* Image Modal */}
        <Dialog
          open={imageModalOpen}
          onClose={handleImageModalClose}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Image sx={{ mr: 1 }} />
              {selectedImage?.filename}
            </Box>
          </DialogTitle>
          <DialogContent>
            {selectedImage && (
              <Box sx={{ textAlign: 'center' }}>
                <Box
                  sx={{
                    backgroundColor: '#000000',
                    display: 'inline-block',
                    padding: '8px',
                    borderRadius: '4px'
                  }}
                >
                  <img
                    src={selectedImage.gcs_url}
                    alt={selectedImage.filename}
                    style={{
                      maxWidth: '100%',
                      maxHeight: '70vh',
                      objectFit: 'contain',
                      display: 'block'
                    }}
                  />
                </Box>
                
                {/* View Image Button */}
                <Box sx={{ mt: 2, textAlign: 'center' }}>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<span>🔗</span>}
                    onClick={() => {
                      window.open(selectedImage.gcs_url, '_blank');
                    }}
                    sx={{ 
                      px: 3, 
                      py: 1,
                      borderRadius: 2,
                      textTransform: 'none',
                      fontWeight: 600
                    }}
                  >
                    Open in New Tab
                  </Button>
                </Box>
                
                <Box sx={{ mt: 2, textAlign: 'left' }}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Upload Date:</strong> {dayjs(selectedImage.upload_date).format('dddd, D MMMM YYYY')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Submission Time:</strong> {dayjs(selectedImage.upload_timestamp).add(7, 'hour').format('HH:mm:ss')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Current Phase:</strong> {phaseLabels[selectedImage.phase]}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>File Size:</strong> {(selectedImage.file_size / 1024).toFixed(2)} KB
                  </Typography>
                </Box>

                {/* Phase Update Section */}
                <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <Typography variant="h6" gutterBottom>
                    Update Phase
                  </Typography>
                  
                  {phaseUpdateError && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      {phaseUpdateError}
                    </Alert>
                  )}

                  <Grid container spacing={2} alignItems="center">
                    {/* Step Back Button - only show for stage1 or winner */}
                    {['stage1', 'winner'].includes(selectedImage.phase) && (
                      <Grid item xs={12} sm={6}>
                        <Button
                          variant="outlined"
                          color="secondary"
                          fullWidth
                          disabled={updatingPhase}
                          onClick={() => handlePhaseUpdate(getPreviousPhase(selectedImage.phase))}
                          startIcon={updatingPhase ? <CircularProgress size={20} /> : null}
                        >
                          {updatingPhase 
                            ? 'Updating...' 
                            : `Step Back to ${phaseLabels[getPreviousPhase(selectedImage.phase)]}`
                          }
                        </Button>
                      </Grid>
                    )}
                    
                    {/* Advance Button - only show if not at winner phase */}
                    {selectedImage.phase !== 'winner' && (
                      <Grid item xs={12} sm={['stage1', 'winner'].includes(selectedImage.phase) ? 6 : 12}>
                        <Button
                          variant="contained"
                          color="primary"
                          fullWidth
                          disabled={updatingPhase}
                          onClick={() => handlePhaseUpdate(getNextPhase(selectedImage.phase))}
                          startIcon={updatingPhase ? <CircularProgress size={20} /> : null}
                        >
                          {updatingPhase 
                            ? 'Updating...' 
                            : `Advance to ${phaseLabels[getNextPhase(selectedImage.phase)]}`
                          }
                        </Button>
                      </Grid>
                    )}
                    

                  </Grid>
                </Box>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleImageModalClose}>Close</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default Dashboard;